// ======================================================================
// FILE:
// src/modules/service_crm/reports/pages/InstalledProductReportPage.js
//
// MODULE:
// Service CRM
//
// FEATURE:
// Installed Product Report
//
// PHASE 1
// - Installed Product Report
// - Search
// - Customer Lookup
// - Pagination
// - Excel Export
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import Select from "react-select";

import reportService
from "../services/reportService";

import businessPartnerService
from "../../../ppe/pages/masters/business_partner/services/businessPartnerService";

export default function InstalledProductReportPage() {

  // =========================================================
  // STATE
  // =========================================================

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [total, setTotal] =
    useState(0);

  const [customerOptions,
    setCustomerOptions] =
    useState([]);

  // =========================================================
  // FILTERS
  // =========================================================

  const [filters, setFilters] =
    useState({

      search: "",

      customer_name: "",

      instance_id: "",

      engine_app_code: "",

      engine_serial_no: "",

      product_status: "ALL",

      commissioning_status: "ALL",

      amc_status: "ALL",

      page: 1,

      page_size: 20,

    });

  // =========================================================
  // LOAD
  // =========================================================

  useEffect(() => {

    loadCustomers();

    loadData();

  }, [filters.page]);

  // =========================================================
  // CUSTOMER LOOKUP
  // =========================================================

  async function loadCustomers() {

    try {

      const rows =
        await businessPartnerService
          .getCustomerLookup();

      const options =
        (rows || []).map(
          (row) => ({

            value:
              row.bp_name,

            label:
              `${row.bp_code} - ${row.bp_name}`,

          })
        );

      setCustomerOptions(
        options
      );

    } catch (err) {

      console.error(err);

    }

  }

  // =========================================================
  // LOAD DATA
  // =========================================================

  async function loadData() {

    try {

      setLoading(true);

      const data =
        await reportService
          .getInstalledProductReport(
            filters
          );

      setRows(
        data?.rows || []
      );

      setTotal(
        data?.total || 0
      );

    } catch (err) {

      console.error(
        err
      );

      alert(
        "Failed to load report"
      );

    } finally {

      setLoading(false);

    }

  }

  // =========================================================
  // EXPORT EXCEL
  // =========================================================

  async function handleExportExcel() {

    try {

      const blob =
        await reportService
          .exportInstalledProductReportExcel(
            filters
          );

      const url =
        window.URL.createObjectURL(
          new Blob([blob])
        );

      const link =
        document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        "installed_product_report.xlsx"
      );

      document.body.appendChild(
        link
      );

      link.click();

      link.remove();

    } catch (err) {

      console.error(
        err
      );

      alert(
        "Failed to export report"
      );

    }

  }

  // =========================================================
  // FILTER CHANGE
  // =========================================================

  function handleFilterChange(
    e
  ) {

    setFilters({

      ...filters,

      [e.target.name]:
        e.target.value,

    });

  }

  // =========================================================
  // SEARCH
  // =========================================================

  function handleSearch() {

    setFilters({

      ...filters,

      page: 1,

    });

    loadData();

  }

  // =========================================================
  // RESET
  // =========================================================

  function handleReset() {

    setFilters({

      search: "",

      customer_name: "",

      instance_id: "",

      engine_app_code: "",

      engine_serial_no: "",

      product_status: "ALL",

      commissioning_status: "ALL",

      amc_status: "ALL",

      page: 1,

      page_size: 20,

    });

  }

  // =========================================================
  // STATUS BADGE
  // =========================================================

  function renderStatus(
    value
  ) {

    if (!value) {

      return "-";

    }

    return (

      <span
        className="badge bg-secondary"
      >
        {value}
      </span>

    );

  }

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div>

      {/* HEADER */}

      <div
        style={{
          marginBottom: 20,
        }}
      >

        <h2
          style={{
            margin: 0,
          }}
        >
          Installed Product Report
        </h2>

        <div
          style={{
            color: "#6c757d",
          }}
        >
          Installed product reporting and analytics
        </div>

      </div>

      {/* FILTERS */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          padding: 16,

          marginBottom: 20,

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",

        }}
      >

        <div
          style={{

            display: "grid",

            gridTemplateColumns:
              "repeat(auto-fit, minmax(220px, 1fr))",

            gap: 14,

          }}
        >

          <input
            type="text"
            className="form-control"
            placeholder="Search"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
          />

          <Select
            options={
              customerOptions
            }
            isClearable
            isSearchable
            placeholder="Customer"
            value={
              customerOptions.find(
                (x) =>
                  x.value ===
                  filters.customer_name
              ) || null
            }
            onChange={(selected) =>
              setFilters({

                ...filters,

                customer_name:
                  selected
                    ?.value || "",

              })
            }
          />

          <input
            type="text"
            className="form-control"
            placeholder="Instance ID"
            name="instance_id"
            value={filters.instance_id}
            onChange={handleFilterChange}
          />

          <input
            type="text"
            className="form-control"
            placeholder="Engine App Code"
            name="engine_app_code"
            value={filters.engine_app_code}
            onChange={handleFilterChange}
          />

          <input
            type="text"
            className="form-control"
            placeholder="Engine Serial No"
            name="engine_serial_no"
            value={filters.engine_serial_no}
            onChange={handleFilterChange}
          />

          <select
            className="form-select"
            name="product_status"
            value={
              filters.product_status
            }
            onChange={
              handleFilterChange
            }
          >
            <option value="ALL">
              All Product Status
            </option>
            <option value="ACTIVE">
              ACTIVE
            </option>
            <option value="INACTIVE">
              INACTIVE
            </option>
          </select>

          <select
            className="form-select"
            name="commissioning_status"
            value={
              filters.commissioning_status
            }
            onChange={
              handleFilterChange
            }
          >
            <option value="ALL">
              All Commissioning
            </option>
            <option value="PENDING">
              PENDING
            </option>
            <option value="COMPLETED">
              COMPLETED
            </option>
          </select>

          <select
            className="form-select"
            name="amc_status"
            value={
              filters.amc_status
            }
            onChange={
              handleFilterChange
            }
          >
            <option value="ALL">
              All AMC
            </option>
            <option value="ACTIVE">
              ACTIVE
            </option>
            <option value="EXPIRED">
              EXPIRED
            </option>
          </select>

        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 10,
          }}
        >

          <button
            className="btn btn-primary"
            onClick={handleSearch}
          >
            Search
          </button>

          <button
            className="btn btn-secondary"
            onClick={handleReset}
          >
            Reset
          </button>

          <button
            className="btn btn-success"
            onClick={
              handleExportExcel
            }
          >
            Export Excel
          </button>

        </div>

      </div>

      {/* TABLE */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          overflow: "hidden",

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",

        }}
      >

        <div
          style={{
            overflowX: "auto",
          }}
        >

          <table
            className="table table-hover"
            style={{
              marginBottom: 0,
            }}
          >

            <thead
              className="table-light"
            >

              <tr>

                <th>Instance ID</th>

                <th>Customer</th>

                <th>Contact Person</th>

                <th>Contact No</th>

                <th>App Code</th>

                <th>Serial No</th>

                <th>City</th>

                <th>State</th>

                <th>Product Status</th>

                <th>Commissioning</th>

                <th>AMC</th>

                <th>Warranty End</th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="12"
                    className="text-center py-4"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="12"
                    className="text-center py-4"
                  >
                    No records found
                  </td>

                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr
                  key={row.id}
                >

                  <td>
                    {row.instance_id}
                  </td>

                  <td>
                    {row.customer_name}
                  </td>

                  <td>
                    {row.contact_person}
                  </td>

                  <td>
                    {row.contact_number}
                  </td>

                  <td>
                    {row.engine_app_code}
                  </td>

                  <td>
                    {row.engine_serial_no}
                  </td>

                  <td>
                    {row.city}
                  </td>

                  <td>
                    {row.state}
                  </td>

                  <td>
                    {renderStatus(
                      row.product_status
                    )}
                  </td>

                  <td>
                    {renderStatus(
                      row.commissioning_status
                    )}
                  </td>

                  <td>
                    {renderStatus(
                      row.amc_status
                    )}
                  </td>

                  <td>
                    {
                      row.warranty_end_date
                    }
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* PAGINATION */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginTop: 16,

        }}
      >

        <div>

          Total:
          {" "}
          {total}

        </div>

        <div
          style={{

            display: "flex",

            gap: 8,

          }}
        >

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              filters.page <= 1
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page - 1,

              })
            }
          >
            Previous
          </button>

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              rows.length <
              filters.page_size
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page + 1,

              })
            }
          >
            Next
          </button>

        </div>

      </div>

    </div>

  );

}